import { ChangeEvent, KeyboardEvent, useCallback, useState } from 'react'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { FormItemProps, InputProps } from 'antd'
import cl from 'classnames'

import InputComponent from 'Shared/atoms/Input'
import ModalNotification from 'Shared/atoms/ModalNotification'
import Icon, { IconType } from 'Shared/atoms/Icon'
import { withTooltip } from 'Shared/HOCs/withTooltip'
import { InputTypes, NumberXYValues } from 'Shared/molecules/Form/types'
import { formatDecimal } from 'Shared/utils/number'
import { hasError } from 'Shared/utils/validation'

import { FieldNameRegexType } from 'Core/interfaces'

import FormItem from './FormItem'

import styles from './input.module.scss'

const Input = withTooltip(InputComponent)

const formulaRegex = /([^a-zA-Z0-9-+*/.)(])/gi

export type FormInputProps<FieldsType extends FieldValues> = InputProps &
	FormItemProps & {
		wrapperClassName?: string
		control: Control<FieldsType>
		fieldName: keyof FieldsType | FieldNameRegexType
		iconType?: IconType
		validate?: boolean
		isNonEditable?: boolean
		showFormulaError?: boolean
		invalidFormulaText?: string
		transformValue?: (value: string) => string | number | null
		type?: InputTypes
		numberXYValues?: NumberXYValues
		showTooltip?: boolean
		helpMessage?: BasicString
		errorMessage?: string
		formatStringCallback?: (value: Key) => string
		tooltipText?: string
		formattedValueCallback?: (value: string | number) => void
	}

function FormInput<FieldsType extends FieldValues>({
	extra,
	label,
	wrapperClassName,
	control,
	fieldName,
	iconType,
	validate,
	transformValue,
	type,
	numberXYValues,
	showFormulaError,
	invalidFormulaText,
	helpMessage,
	errorMessage,
	formattedValueCallback,
	formatStringCallback,
	...props
}: FormInputProps<FieldsType>) {
	const IconComponent = iconType ? <Icon type={iconType} className={styles.icon} /> : null
	const [previousValue, setPreviousValue] = useState<string>('')

	const handleKeyPress = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (
				(numberXYValues &&
					numberXYValues.afterDot > 0 &&
					event.key === '.' &&
					previousValue.indexOf('.') !== -1) ||
				(numberXYValues?.allowNegative &&
					event.key === '-' &&
					previousValue.indexOf('-') !== -1) ||
				(!(event.target as HTMLInputElement).value && event.key === '.')
			) {
				event.preventDefault()
				return
			}
		},
		[numberXYValues, previousValue]
	)

	return (
		<Controller
			name={fieldName as FieldPath<FieldsType>}
			control={control}
			render={({
				field: { onChange, name, value, ...fieldProps },
				fieldState: { error }
			}) => {
				const correctValue =
					formatStringCallback && value ? formatStringCallback(value) : value

				const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
					const { value } = event.target
					let formatted = ''

					switch (type) {
						case InputTypes.integer:
							if (value?.match(/^[0-9]+$/) || !value) {
								onChange(value)
							}
							break
						case InputTypes.formula:
							formatted = value.replace(formulaRegex, '')
							onChange(formatted)
							break
						case InputTypes.numberXY:
							formatted = formatDecimal(
								value,
								numberXYValues!.total,
								numberXYValues!.afterDot,
								!!numberXYValues!.allowNegative
							)
							setPreviousValue(value)
							onChange(formatted)
							break
						default:
							onChange(transformValue ? transformValue(value) : value)
					}

					props.onChange?.()
					formattedValueCallback?.(formatted)
				}
				return (
					<FormItem
						className={cl(styles.inputWrapper, wrapperClassName)}
						label={label}
						colon={false}
						error={hasError(error)}
						extra={extra}
						name={name}
						validate={validate}
						helpMessage={helpMessage}
						errorMessage={errorMessage}
					>
						{type === InputTypes.formula && showFormulaError && (
							<ModalNotification
								type="error"
								condition={hasError(error)}
								text={invalidFormulaText}
							/>
						)}
						<Input
							{...fieldProps}
							{...props}
							value={correctValue}
							onChange={handleChange}
							prefix={props.prefix || IconComponent}
							onKeyPress={handleKeyPress}
						/>
					</FormItem>
				)
			}}
		/>
	)
}

export default FormInput
